/**
 * Declarative front-end analytics events.
 *
 * Any element carrying `data-track-event="WEB_…"` (optionally with a
 * `data-track-props='{"k":"v"}'` JSON payload) fires a RudderStack `track`
 * on click, via event delegation. This keeps the event list in the markup and
 * avoids scattering tracking calls across many handlers.
 */
import { trackEvent } from "./rudderstack-client";

interface TrackTarget {
    getAttribute(name: string): string | null;
}

export function parseTrackProps(raw: string | null): Record<string, unknown> {
    if (!raw) {
        return {};
    }

    try {
        const parsed = JSON.parse(raw);

        return parsed && typeof parsed === "object" ? (parsed as Record<string, unknown>) : {};
    } catch {
        return {};
    }
}

export function resolveTrackEvent(
    element: TrackTarget | null,
): { event: string; props: Record<string, unknown> } | null {
    if (!element) {
        return null;
    }

    const event = element.getAttribute("data-track-event");
    if (!event) {
        return null;
    }

    return { event, props: parseTrackProps(element.getAttribute("data-track-props")) };
}

/** Resolve and fire the event declared on the given element (no-op if none). */
export function trackFromElement(element: TrackTarget | null): void {
    const resolved = resolveTrackEvent(element);
    if (resolved) {
        trackEvent(resolved.event, resolved.props);
    }
}

function handleClick(event: Event): void {
    const target = event.target;
    if (!(target instanceof Element)) {
        return;
    }

    trackFromElement(target.closest("[data-track-event]"));
}

/** Attach the delegated click listener. Idempotent across calls. */
export function initAnalyticsEvents(): void {
    if (typeof document === "undefined") {
        return;
    }

    document.addEventListener("click", handleClick, { capture: true, passive: true });
}
